// contexts/AuthContext.js - FIXED VERSION
import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { API_CONFIG } from "./apiConfig";
import { setToken, getToken, removeToken } from "./tokenStorage";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Helper to resolve token from cookies or localStorage
  const getAnyToken = () => {
    return (
      getToken("accessToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      null
    );
  };

  // Helper to decode JWT payload
  const decodeJwtPayload = (token) => {
    try {
      if (!token) return null;
      const base64Url = token.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Create the axios instance (and its interceptors) exactly once per
  // provider mount via a ref, instead of on every render — a fresh
  // instance each render gave every consumer of useAuth().api a new
  // object identity, which broke their effect/useCallback dependency
  // arrays and caused API calls (and R2 list calls) to fire 2-3x per
  // page load.
  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      withCredentials: API_CONFIG.withCredentials,
    });

    // Request interceptor - add auth token from storage
    instance.interceptors.request.use(
      (config) => {
        const token = getAnyToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // ✅ FIXED: Response interceptor - handle token refresh WITHOUT infinite loop
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // ✅ ADDED: Skip interceptor for login and logout endpoints to prevent loops
        if (
          originalRequest.url.includes("/login") ||
          originalRequest.url.includes("/logout")
        ) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshTokenValue = getToken("refreshToken");
            if (!refreshTokenValue) {
              // ✅ CHANGED: Don't call logout, just reject
              console.warn("No refresh token available");
              return Promise.reject(error);
            }

            const response = await axios.post(
              `${API_CONFIG.baseURL}/refresh`,
              {},
              {
                headers: { Authorization: `Bearer ${refreshTokenValue}` },
                withCredentials: API_CONFIG.withCredentials,
              }
            );

            const { data } = response;
            if (data.success) {
              const { accessToken, refreshToken: newRefreshToken } = data.data;

              // Store new tokens using our cross-platform storage
              setToken("accessToken", accessToken);
              setToken("refreshToken", newRefreshToken);

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return instance(originalRequest);
            } else {
              // ✅ CHANGED: Don't call logout, just reject
              console.warn("Token refresh failed - no success in response");
              return Promise.reject(error);
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
            // ✅ CHANGED: Don't call logout in interceptor - let the component handle it
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    apiRef.current = instance;
  }
  const api = apiRef.current;

  const login = async (credentials) => {
    try {
      // ✅ FIXED: Use direct axios call for login to avoid interceptor issues
      const response = await axios.post(
        `${API_CONFIG.baseURL}/login`,
        credentials,
        {
          withCredentials: API_CONFIG.withCredentials,
          timeout: API_CONFIG.timeout,
        }
      );

      const { data } = response;

      if (data.success) {
        const { accessToken, refreshToken, ...userData } = data.data;

        // Store tokens using cross-platform storage
        setToken("accessToken", accessToken);
        setToken("refreshToken", refreshToken);

        setUser(userData);
        setIsAuthenticated(true);

        return {
          success: true,
          data: userData,
          userRole: userData.userRole,
        };
      } else {
        return {
          success: false,
          message: data.message || "Login failed",
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.message || "Login failed";
      return {
        success: false,
        message,
        status: error.response?.status,
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      return (
        error.response?.data || {
          success: false,
          message: "Registration failed",
        }
      );
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const response = await api.post("/auth/verify-otp", { email, otp });
      const { data } = response;

      if (data.success) {
        const { accessToken, refreshToken, ...userData } = data.data;

        setToken("accessToken", accessToken);
        setToken("refreshToken", refreshToken);

        setUser(userData);
        setIsAuthenticated(true);
      }
      return data;
    } catch (error) {
      return (
        error.response?.data || {
          success: false,
          message: "OTP verification failed",
        }
      );
    }
  };

  const getCurrentUser = async () => {
    try {
      const response = await api.get("/auth/me");
      const { data } = response;

      if (data.success) {
        setUser(data.data);
        setIsAuthenticated(true);
        return { success: true, data: data.data };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to get user",
      };
    }
  };

  const logout = async () => {
    try {
      // ✅ FIXED: Use direct axios call for logout to avoid interceptor
      await axios.post(
        `${API_CONFIG.baseURL}/logout`,
        {},
        {
          withCredentials: API_CONFIG.withCredentials,
          headers: {
            Authorization: `Bearer ${getToken("accessToken")}`,
          },
        }
      );
    } catch (error) {
      console.error("Backend logout error:", error);
    } finally {
      // Clear tokens from storage
      removeToken("accessToken");
      removeToken("refreshToken");

      // Clear state
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Validate token by calling backend (NO JWT DECODING)
  const validateToken = async () => {
    try {
      const response = await api.post("/auth/validate");
      return response.data.success;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getAnyToken();

      if (token) {
        const payload = decodeJwtPayload(token);
        if (payload) {
          // Check expiration
          const now = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp < now) {
            console.warn("Token expired on init");
            setUser(null);
            setIsAuthenticated(false);
          } else {
            setUser({
              userId: payload.userId,
              userRole: payload.userRole || payload.role,
              role: payload.userRole || payload.role,
              firstName: payload.firstName,
              email: payload.sub,
            });
            setIsAuthenticated(true);
            // Optionally refresh full profile in background
            getCurrentUser().catch(() => {});
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    verifyOtp,
    logout,
    getCurrentUser,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
